import React, { useContext, useState, MouseEvent, ReactNode, Fragment } from 'react';
import { Card, ListItemIcon, MenuItem, Menu, Typography, makeStyles } from '@material-ui/core';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import { Link } from 'react-router-dom';
import { LaunchOutlined } from '@material-ui/icons';
import AppHeader from './main/AppHeader';
import LocalizationContext from '../contexts/LocalizationContext';
import AppSubHeader from './main/AppSubHeader';
import { IApps, ISubAppLayouts, LayoutName } from './apps/account/interfaces/IAppLayouts';
import { giveAppsIcons } from 'src/utilities/FunctionCss/FunctionAppsIcons';
import { AccountContext } from './apps/account/context/AccountProvider';
import AppsEnum from 'src/enums/AppsEnum';

const useStyles = makeStyles({
    root: {
        minWidth: 275,
        margin: '5px -10px',
        minHeight: '64px',
    },
    title: {
        fontSize: 14,
    },
    url: {
        marginLeft: 2,
        fontSize: 14,
    },
    iconContainer: {
        borderRadius: '4px 0 0 4px',
        minHeight: '64px',
        width: '64px',
        position: 'absolute',
        left: '0',
        top: '0',
        bottom: '0',
    },
    subApp: {},
    subAppsContainer: {
        margin: '-3px -10px 0',
    },
    subAppTitle: {
        fontWeight: 400,
        textDecoration: 'none',
        color: 'black',
    },
    subAppIcon: {
        color: '#444',
    },
});

interface ApplicationsListProps {
    apps: IApps[];
}

const ApplicationsList = ({ apps }: ApplicationsListProps) => {
    const localization = useContext(LocalizationContext);
    const account = useContext(AccountContext);

    const listSse: IApps[] = apps
        .filter((a: IApps) => a.appsLayoutGroups.nameEn === LayoutName.sse)
        .sort((a: IApps, b: IApps) => a.icon - b.icon);
    const listRh: IApps[] = apps
        .filter((a: IApps) => a.appsLayoutGroups.nameEn === LayoutName.rh)
        .sort((a: IApps, b: IApps) => a.icon - b.icon);
    const listProj: IApps[] = apps
        .filter((a: IApps) => a.appsLayoutGroups.nameEn === LayoutName.proj)
        .sort((a: IApps, b: IApps) => a.icon - b.icon);

    return (
        <>
            <div style={{ marginTop: '50px' }}>
                <AppHeader
                    name={localization.strings.applications}
                    style={{ marginBottom: '10px', color: '#222' }}
                    appName={account.apps.find((a: IApps) => a.name === AppsEnum.home)?.name ?? AppsEnum.home}
                    appId={account.apps.find((a: IApps) => a.name === AppsEnum.home)?.id}
                    subAppId={null}
                />
            </div>

            <LayeringOfApps apps={listSse} />
            <LayeringOfApps apps={listRh} />
            <LayeringOfApps apps={listProj} />
        </>
    );
};

const LayeringOfApps = ({ apps }: { apps: IApps[] }) => {
    const localization = useContext(LocalizationContext);
    const account = useContext(AccountContext);

    const classes = useStyles();

    return (
        <Fragment key={apps[0].appsLayoutGroups.nameEn}>
            <AppSubHeader name={localization.lang === 'fr' ? apps[0].appsLayoutGroups.nameFr : apps[0].appsLayoutGroups.nameEn} />
            <Row style={{ marginLeft: 0, marginRight: 0, marginBottom: '20px' }}>
                {apps
                    .filter((app) => {
                        return (
                            (app.name !== AppsEnum.synergy &&
                                app.name !== AppsEnum.mfilesDocuments &&
                                app.name !== AppsEnum.hierarchy &&
                                app.name !== AppsEnum.documentsRebranding &&
                                app.name !== AppsEnum.nearMisses) ||
                            (app.name === AppsEnum.nearMisses && account.account?.isHealthAndSafety) ||
                            ((app.name === AppsEnum.mfilesDocuments || app.name === AppsEnum.documentsRebranding) &&
                                account.account?.isDocuments) ||
                            (app.name === AppsEnum.synergy && account.account?.isSynergy) ||
                            (app.name === AppsEnum.hierarchy && account.account?.isSynergy)
                        );
                    })
                    .map((app: IApps) => (
                        <Col key={app.nameEn} lg={6} md={12}>
                            <AppLinkType app={app}>
                                <Card className={classes.root} style={{ opacity: app.enabled ? '1' : '0.4' }}>
                                    <div style={{ padding: '16px', position: 'relative' }}>
                                        <div className={classes.iconContainer} style={{ backgroundColor: app.color }}>
                                            {giveAppsIcons(app.icon)}
                                        </div>
                                        <div style={{ display: 'inline-block', marginLeft: '68px' }}>
                                            <Typography variant='h6' component='h2' style={{ color: '#666', fontWeight: 400 }}>
                                                {localization.lang === 'fr' ? app.nameFr : app.nameEn}
                                            </Typography>
                                            <Typography className={classes.url} color='textSecondary'>
                                                {'externalUrl' in app && app.externalUrl && (
                                                    <>
                                                        {localization.strings.externalApp}
                                                        <LaunchOutlined style={{ fontSize: '14px', marginLeft: '10px' }} />
                                                    </>
                                                )}
                                            </Typography>
                                        </div>
                                    </div>
                                </Card>
                            </AppLinkType>
                        </Col>
                    ))}
            </Row>
        </Fragment>
    );
};

const AppLinkType = ({ app, children }: { app: IApps; children: ReactNode }) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLElement) | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    if (!app.enabled) return <>{children}</>;

    return (
        <>
            {app.url !== null && app.url !== undefined ? (
                <Link to={app.url}>{children}</Link>
            ) : (
                <>
                    <div onClick={handleClick}>{children}</div>
                    {app.subApps && (
                        <Menu
                            open={open}
                            onClose={() => setAnchorEl(null)}
                            anchorEl={anchorEl as any}
                            anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'center', horizontal: 'center' }}
                            getContentAnchorEl={undefined}
                            className={classes.subAppsContainer}>
                            {app.subApps
                                .sort((a: ISubAppLayouts, b: ISubAppLayouts) => a.icon - b.icon)
                                .map((subApp: ISubAppLayouts) => (
                                    <Link
                                        key={subApp.nameEn}
                                        className={`no-link${subApp.enabled ? '' : ' no-pointer'}`}
                                        to={subApp.enabled ? subApp.url : ''}>
                                        <SubApp subApp={subApp} />
                                    </Link>
                                ))}
                        </Menu>
                    )}
                </>
            )}
        </>
    );
};

interface SubAppProps {
    subApp: ISubAppLayouts;
}

const SubApp = ({ subApp }: SubAppProps) => {
    const classes = useStyles();
    const localization = useContext(LocalizationContext);

    return (
        <MenuItem button className={classes.subApp} disabled={!subApp.enabled}>
            <ListItemIcon className={classes.subAppIcon}>{giveAppsIcons(subApp.icon)}</ListItemIcon>
            <div className={classes.subAppTitle}>{localization.lang === 'fr' ? subApp.nameFr : subApp.nameEn}</div>
        </MenuItem>
    );
};

export default ApplicationsList;
